import { useDecryptedImage } from "../hooks/useDecryptedImage";
import { Avatar } from "./Avatar";
import { Spinner } from "./ui/Spinner";

interface DecryptedAvatarProps {
  // Opaque ciphertext blob reference (a server-minted proxy URL), not a directly
  // renderable image URL.
  token: string | null | undefined;
  name?: string;
  size?: "tiny" | "small" | "large";
  onClick?: () => void;
}

const SPINNER_SIZE: Record<NonNullable<DecryptedAvatarProps["size"]>, number> = {
  tiny: 12,
  small: 16,
  large: 28,
};

// Avatar whose source is an encrypted blob: useDecryptedImage fetches + decrypts
// the token into a short-lived object URL. While that is in flight we show a
// Spinner inside the avatar circle; on failure (or no token) we fall back to
// Avatar's initials by passing a null src.
export function DecryptedAvatar({ token, name = "", size = "small", onClick }: DecryptedAvatarProps) {
  const { url, loading, error } = useDecryptedImage(token ?? null);

  if (loading) {
    return (
      <div
        className={`avatar avatar-${size}`}
        onClick={onClick}
        style={onClick ? { cursor: "pointer" } : undefined}
        role={onClick ? "button" : undefined}
        aria-label="Loading photo"
      >
        <Spinner size={SPINNER_SIZE[size]} />
      </div>
    );
  }

  return <Avatar src={error ? null : url} name={name} size={size} onClick={onClick} />;
}
